import { Link, Navigate, NavLink, Outlet } from "react-router-dom";
import { HelpCircle, Home, MessageCircle, Truck, User, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/hooks/useNotifications";
import { useProperty } from "@/context/PropertyContext";
import { SkipLink } from "@/components/a11y/SkipLink";

const ALL_TABS = [
  { to: "/student/home", icon: Home, label: "Home", featureKey: null as string | null },
  { to: "/student/transport", icon: Truck, label: "Transport", featureKey: "transport" },
  { to: "/student/maintenance", icon: Wrench, label: "Maintenance", featureKey: null },
  { to: "/student/chat", icon: MessageCircle, label: "Chat", featureKey: null },
  { to: "/student/profile", icon: User, label: "Profile", featureKey: null },
];

export function StudentLayout() {
  const { profile } = useAuth();
  const { unreadCount } = useNotifications();
  const { property } = useProperty();

  // Belt-and-braces: students whose onboarding form is still in review
  // (status='pending' for the form itself or 'pending_approval' awaiting
  // admin sign-off) should never see the bottom-tab student shell — they
  // get bounced back to the pending status page. ProtectedRoute already
  // handles this on /student/* but this guard prevents any future bypass.
  const status = profile?.status ?? null;
  if (status === "pending" || status === "pending_approval") {
    return <Navigate to="/onboarding/pending" replace />;
  }
  if (status === "rejected") {
    return <Navigate to="/onboarding/rejected" replace />;
  }

  // Filter tabs by per-property feature flags. Default to "show" while
  // the property is still loading so the nav doesn't flicker; we'll
  // re-render once `property` arrives. Hide cleanly when a feature is
  // explicitly disabled.
  const TABS = ALL_TABS.filter((t) => {
    if (t.featureKey === "transport") {
      // If we know transport is disabled, hide. If property hasn't loaded
      // yet (undefined), fall back to whatever the row says or hide
      // optimistically. Showing-then-hiding looks worse than the reverse.
      if (property && property.transport_enabled === false) return false;
    }
    return true;
  });
  // Tailwind needs a stable class string, not a dynamic template literal.
  const colsClass =
    TABS.length === 5
      ? "grid-cols-5"
      : TABS.length === 4
        ? "grid-cols-4"
        : "grid-cols-3";

  return (
    <div className="min-h-screen flex flex-col bg-black">
      <SkipLink />
      <header className="sticky top-0 z-30 bg-surface border-b border-border px-5 py-2 flex items-center justify-between" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <span className="text-xs uppercase tracking-wider text-muted">
          {property?.name ?? ""}
        </span>
        {/* Help — student layout has no sidebar, so we surface Help in the
            sticky header instead of the bottom-tab bar (which is at max
            tabs already). Sign-out lives on the Profile tab. */}
        <Link
          to="/help"
          aria-label="Help and support"
          className="inline-flex items-center justify-center h-9 w-9 rounded-md text-muted hover:text-gold hover:bg-surface2 gold-focus"
        >
          <HelpCircle className="h-4 w-4" />
        </Link>
      </header>
      <main id="main" className="flex-1 overflow-y-auto pb-[80px]">
        <Outlet />
      </main>

      <nav
        className="fixed bottom-0 inset-x-0 z-40 bg-surface border-t border-border safe-bottom"
        aria-label="Student navigation"
      >
        <div className={cn("grid max-w-[480px] mx-auto", colsClass)}>
          {TABS.map((t) => {
            const showBadge = t.to === "/student/chat" && unreadCount > 0;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                className={({ isActive }) =>
                  cn(
                    "flex flex-col items-center justify-center gap-1 py-2.5 min-h-[56px] relative",
                    isActive ? "text-gold" : "text-muted hover:text-text",
                  )
                }
              >
                <t.icon className="h-5 w-5" />
                <span className="text-[10px] font-semibold uppercase tracking-wider">
                  {t.label}
                </span>
                {showBadge && (
                  <span className="absolute top-2 right-1/4 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-gold text-[10px] font-bold text-black">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
